import React, { useState } from "react";
import { Table, Pagination } from "flowbite-react";
import SidbarComponent from "./SidbarComponent";
import TopBarComponent from "./TopBarComponent";
import { FooterComponents } from "./FooterComponent";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Search, DollarSign, Users, Loader2, Filter, Download, ArrowLeft, CheckCircle, XCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { getToken } from "../utils/auth";

export default function LocalLoan() {
  const [localLoans, setLocalLoans] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const categories = JSON.parse(sessionStorage.getItem("categories"));
  const token = getToken();

  const fetchLocalLoans = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL +
          `hec/localLoan/getLocalLoansByCategoryAndAcademicYear?category=${categories.section}&academicYear=${categories.academicYear}`,
        {
          headers: {
            Authorization: "Bearer " + token["message"],
          },
        }
      );
      const localLoans = await response.data;
      setLocalLoans(localLoans);
      return localLoans;
    } catch (error) {
      toast.error("Failed to fetch local loans", {
        position: "top-center",
        autoClose: 5000,
        theme: "colored",
      });
      return null;
    }
  };

  const { isPending, error, data } = useQuery({
    queryKey: ["localLoans"],
    queryFn: () => fetchLocalLoans(),
    retry: 3,
    staleTime: 10000,
  });

  // Filter loans based on search term
  const filteredLoans = localLoans.filter(data =>
    data.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    data.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    data.regNumber?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination calculations
  const totalItems = filteredLoans.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredLoans.slice(startIndex, endIndex);

  // Handle page change
  const onPageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1); // Reset to first page
  };

  // Reset pagination when search term changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const getScoreColor = (score) => {
    if (score >= 80) return 'bg-green-100 text-green-800 border-green-200';
    if (score >= 60) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };

  const getStatusBadge = (status) => {
    if (status === 'APPROVED') {
      return (
        <span className="inline-flex items-center space-x-1 px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 border border-green-200">
          <CheckCircle className="w-3 h-3" />
          <span>Approved</span>
        </span>
      );
    } else if (status === 'REJECTED') {
      return (
        <span className="inline-flex items-center space-x-1 px-3 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 border border-red-200">
          <XCircle className="w-3 h-3" />
          <span>Rejected</span>
        </span>
      );
    } else {
      return (
        <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200">
          Pending
        </span>
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50 flex flex-col">
      <TopBarComponent />
      <aside
        id="logo-sidebar"
        className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0"
        aria-label="Sidebar"
      >
        <SidbarComponent />
      </aside>

      <div className="p-6 sm:ml-64 transition-all duration-300 flex-1 flex flex-col">
        {/* Page Header */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <DollarSign className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Local Loans</h1>
                  <p className="text-gray-600 mt-1">
                    {categories?.section} - {categories?.academicYear} Academic Year
                  </p>
                </div>
              </div>
              <div className="mt-6 sm:mt-0 flex items-center space-x-4">
                <div className="flex items-center space-x-3 text-sm text-gray-600 bg-gradient-to-r from-cyan-50 to-blue-50 px-4 py-3 rounded-xl border border-cyan-200">
                  <Users className="w-5 h-5 text-cyan-600" />
                  <span className="font-semibold">
                    {searchTerm ? `${filteredLoans.length} of ${localLoans.length}` : localLoans.length} Applications
                  </span>
                </div>
                <button className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-xl hover:from-cyan-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02]">
                  <Download className="w-5 h-5" />
                  <span>Export</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          {/* Search Header */}
          <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border-b border-gray-200 p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="relative max-w-md flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all duration-200 shadow-sm placeholder-gray-500"
                  placeholder="Search by name or registration number..."
                />
              </div>
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Show:</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                    className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all duration-200"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-sm text-gray-700">per page</span>
                </div>
                <button className="flex items-center space-x-2 px-5 py-3 text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-all duration-200 shadow-sm">
                  <Filter className="w-4 h-4" />
                  <span>Filter</span>
                </button>
              </div>
            </div>
          </div>

          {/* Table Content */}
          <div className="p-6">
            {isPending ? (
              <div className="flex items-center justify-center py-24">
                <div className="text-center">
                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-r from-cyan-200 to-blue-200 rounded-full animate-pulse mx-auto mb-4"></div>
                    <Loader2 className="w-8 h-8 text-cyan-600 animate-spin absolute top-4 left-1/2 transform -translate-x-1/2" />
                  </div>
                  <p className="text-gray-700 text-xl font-semibold mb-2">Loading Local Loans</p>
                  <p className="text-gray-500">Please wait while we fetch the latest data...</p>
                </div>
              </div>
            ) : token?.user?.principalRole?.roleName === "Admin" || token?.user?.principalRole?.roleName === "DG" ? (
              <div>
                {filteredLoans.length > 0 ? (
                  <>
                    {/* Enhanced Table */}
                    <div className="overflow-hidden border border-gray-200 rounded-xl shadow-sm">
                      <Table striped className="w-full">
                        <Table.Head className="bg-gradient-to-r from-gray-50 to-cyan-50">
                          <Table.HeadCell className="text-gray-700 font-semibold">Registration #</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Student Name</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Gender</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Study Area</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Marks</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Performance</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Qualification</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Total Score</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Combination</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Year</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Status</Table.HeadCell>
                        </Table.Head>
                        <Table.Body className="divide-y divide-gray-200">
                          {currentItems.map((data, index) => (
                            <Table.Row
                              className="bg-white hover:bg-gradient-to-r hover:from-cyan-50 hover:to-blue-50 transition-all duration-200 border-b border-gray-100"
                              key={data.regNumber}
                            >
                              <Table.Cell className="whitespace-nowrap">
                                <div className="font-semibold text-gray-900 text-sm">
                                  {data.regNumber}
                                </div>
                              </Table.Cell>
                              <Table.Cell>
                                <div className="flex items-center space-x-3">
                                  <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">
                                    {data.firstName?.charAt(0)}{data.lastName?.charAt(0)}
                                  </div>
                                  <div>
                                    <div className="font-semibold text-gray-900 text-sm">
                                      {data.firstName} {data.lastName}
                                    </div>
                                  </div>
                                </div>
                              </Table.Cell>
                              <Table.Cell>
                                <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                                  data.gender === 'Male' 
                                    ? 'bg-blue-100 text-blue-800 border border-blue-200' 
                                    : 'bg-pink-100 text-pink-800 border border-pink-200'
                                }`}>
                                  {data.gender}
                                </span>
                              </Table.Cell>
                              <Table.Cell className="text-gray-600 text-sm font-medium">
                                {data.studyAreaId}
                              </Table.Cell>
                              <Table.Cell>
                                <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${getScoreColor(data.marks)} border`}>
                                  {data.marks}
                                </span>
                              </Table.Cell>
                              <Table.Cell>
                                <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${getScoreColor(data.performanceScore)} border`}>
                                  {data.performanceScore}
                                </span>
                              </Table.Cell>
                              <Table.Cell>
                                <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${getScoreColor(data.qualificationScore)} border`}>
                                  {data.qualificationScore}
                                </span>
                              </Table.Cell>
                              <Table.Cell>
                                <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${getScoreColor(data.totalScore)} border`}>
                                  {data.totalScore}
                                </span>
                              </Table.Cell>
                              <Table.Cell className="text-gray-600 text-sm font-medium">
                                {data.combinationId}
                              </Table.Cell>
                              <Table.Cell className="text-gray-600 text-sm font-medium">
                                {data.completionYearId}
                              </Table.Cell>
                              <Table.Cell>
                                {getStatusBadge(data.status)}
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table>
                    </div>
                    
                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                        {/* Results Summary */}
                        <div className="text-sm text-gray-700">
                          Showing <span className="font-semibold">{startIndex + 1}</span> to{' '}
                          <span className="font-semibold">{Math.min(endIndex, totalItems)}</span> of{' '}
                          <span className="font-semibold">{totalItems}</span> results
                          {searchTerm && (
                            <span className="text-gray-500 ml-1">(filtered from {localLoans.length} total)</span>
                          )}
                        </div>
                        
                        {/* Pagination Component */}
                        <div className="flex items-center space-x-3">
                          <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={onPageChange}
                            previousLabel=""
                            nextLabel=""
                            className="flex items-center"
                            theme={{
                              pages: {
                                base: "xs:mt-0 mt-2 inline-flex items-center -space-x-px",
                                showIcon: "inline-flex items-center justify-center px-3 py-2 text-sm leading-tight text-gray-500 bg-white border border-gray-300 hover:bg-gray-100 hover:text-gray-700 transition-colors duration-200",
                                previous: "inline-flex items-center justify-center px-3 py-2 ml-0 text-sm leading-tight text-gray-500 bg-white border border-gray-300 rounded-l-lg hover:bg-gray-100 hover:text-gray-700 transition-colors duration-200",
                                next: "inline-flex items-center justify-center px-3 py-2 text-sm leading-tight text-gray-500 bg-white border border-gray-300 rounded-r-lg hover:bg-gray-100 hover:text-gray-700 transition-colors duration-200",
                                selector: "inline-flex items-center justify-center px-3 py-2 text-sm leading-tight text-gray-500 bg-white border border-gray-300 hover:bg-gray-100 hover:text-gray-700 transition-colors duration-200",
                                active: "inline-flex items-center justify-center px-3 py-2 text-sm leading-tight text-white bg-gradient-to-r from-cyan-600 to-blue-600 border border-cyan-600 hover:from-cyan-700 hover:to-blue-700 transition-all duration-200"
                              }
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-24">
                    <div className="w-20 h-20 bg-gradient-to-r from-gray-200 to-cyan-200 rounded-full flex items-center justify-center mx-auto mb-6">
                      <DollarSign className="w-10 h-10 text-gray-400" />
                    </div>
                    <p className="text-gray-700 text-xl font-semibold mb-2">No Loans Found</p>
                    <p className="text-gray-500 max-w-md mx-auto">
                      {searchTerm 
                        ? 'No loans match your search criteria. Try adjusting your search terms.' 
                        : 'No local loans are available for the selected category and academic year.'
                      }
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-24">
                <div className="w-20 h-20 bg-gradient-to-r from-red-200 to-pink-200 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="w-10 h-10 text-red-400" />
                </div>
                <p className="text-red-600 text-xl font-semibold mb-2">Access Denied</p>
                <p className="text-gray-600">You don't have permission to view this page</p>
              </div>
            )}

            {/* Back Button */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <a
                href="/loanApprovalStep"
                className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl hover:from-gray-700 hover:to-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] font-medium"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Loan Approval</span>
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <FooterComponents />
      </div>
    </div>
  );
}
